import { useCallback, useEffect, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { useI18n } from '../lib/i18n';

/**
 * "HAY UNA VERSIÓN NUEVA" — y la toma sola.
 *
 * El problema real: la app es una SPA y la gente no la recarga. Alguien que abrió la pestaña el
 * lunes sigue el jueves ejecutando el JavaScript del lunes, así que ninguna corrección le llega —
 * ni siquiera la corrección de "los avisos deben aparecer sin recargar". Se vio con el anuncio
 * interno: se publicó para 46 personas y lo vieron 8, las que por casualidad recargaron.
 *
 * CÓMO SE DETECTA, sin depender de ninguna variable de build: se pide el `index.html` del servidor
 * y se mira el nombre del bundle principal, que lleva un hash y cambia en cada deploy. Si el que
 * sirve el servidor no es el que esta pestaña tiene cargado, hay versión nueva. Comparar hashes de
 * archivos no puede desincronizarse con el deploy; una variable de entorno mal seteada sí, y ahí el
 * costo es un bucle de recargas.
 *
 * CUÁNDO SE RECARGA. Nunca en medio del trabajo: recargar mientras alguien escribe un correo le
 * borra el correo. Solo cuando VUELVE a la pestaña después de un rato largo — estuvo afuera, no
 * hay nada a medio tipear. En cualquier otro momento aparece la barra y decide la persona.
 *
 * Contra bucles: si tras recargar el servidor sigue anunciando otro bundle (deploy a medias, CDN
 * con caché vieja), NO se recarga de nuevo — se deja la barra y listo. Una pestaña recargándose
 * sola en loop es peor que una desactualizada.
 */
const CADA_MS = 5 * 60_000;
const AUSENTE_MS = 2 * 60_000; // cuánto tiene que haber estado afuera para recargar sin preguntar
const YA_INTENTE = 'app.update.tried';

/** El bundle principal que ESTA pestaña tiene cargado. */
function bundleActual(): string | null {
  const s = document.querySelector<HTMLScriptElement>('script[type="module"][src]');
  return s ? new URL(s.src, location.origin).pathname : null;
}

/** El bundle principal que el SERVIDOR está sirviendo ahora. */
async function bundleDelServidor(): Promise<string | null> {
  const r = await fetch(`/index.html?_=${Date.now()}`, { cache: 'no-store' });
  if (!r.ok) return null;
  const html = await r.text();
  // Independiente del orden de atributos: Vite hoy emite type antes que src, pero atarse a ese
  // orden es firmar que un cambio de bundler rompa la detección en silencio.
  for (const tag of html.match(/<script\b[^>]*>/gi) ?? []) {
    if (!/type=["']module["']/i.test(tag)) continue;
    const src = tag.match(/\bsrc=["']([^"']+)["']/i)?.[1];
    if (src) return new URL(src, location.origin).pathname;
  }
  return null;
}

export function AppUpdateBar() {
  const { t } = useI18n();
  const [hay, setHay] = useState(false);
  const mio = useRef<string | null>(null);
  const oculto = useRef<number | null>(null);

  const revisar = useCallback(async () => {
    if (!mio.current) return;
    try {
      const suyo = await bundleDelServidor();
      if (suyo && suyo !== mio.current) setHay(true);
    } catch { /* sin red no se molesta a nadie */ }
  }, []);

  const recargar = useCallback(() => {
    try { sessionStorage.setItem(YA_INTENTE, mio.current ?? '1'); } catch { /* modo privado */ }
    location.reload();
  }, []);

  useEffect(() => {
    mio.current = bundleActual();
    // Si ya se recargó por ESTE mismo bundle y sigue igual, no se insiste: algo del lado del
    // servidor no terminó de publicar y recargar otra vez no lo va a arreglar.
    let yaIntento = false;
    try { yaIntento = sessionStorage.getItem(YA_INTENTE) === mio.current; } catch { /* */ }

    void revisar();
    const iv = window.setInterval(() => { if (!document.hidden) void revisar(); }, CADA_MS);

    const alCambiarVisibilidad = () => {
      if (document.hidden) { oculto.current = Date.now(); return; }
      const afuera = oculto.current ? Date.now() - oculto.current : 0;
      oculto.current = null;
      void (async () => {
        await revisar();
        // Volvió después de un rato: momento seguro para tomar la versión nueva sin preguntar.
        const suyo = await bundleDelServidor().catch(() => null);
        if (suyo && mio.current && suyo !== mio.current && afuera >= AUSENTE_MS && !yaIntento) recargar();
      })();
    };
    document.addEventListener('visibilitychange', alCambiarVisibilidad);
    return () => {
      window.clearInterval(iv);
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
    };
  }, [revisar, recargar]);

  if (!hay) return null;
  return (
    <div role="status" style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
      background: '#1e3a5f', color: '#fff', padding: '6px 12px', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)',
    }}>
      <span><RotateCcw size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('upd.available')}</span>
      <button onClick={recargar} style={{
        border: 0, borderRadius: 7, padding: '3px 12px', cursor: 'pointer',
        background: '#fff', color: '#1e3a5f', font: 'inherit', fontWeight: 800,
      }}>{t('upd.reload')}</button>
    </div>
  );
}
